import { useEffect, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  /** Modal width - defaults to 'lg' */
  size?: ModalSize
  /** Whether clicking the backdrop closes the modal - defaults to true */
  closeOnBackdrop?: boolean
  /** Whether pressing Escape closes the modal - defaults to true */
  closeOnEscape?: boolean
  /** Additional classes for the modal container */
  className?: string
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: 'max-w-full mx-4'
}

/**
 * Shared Modal component that renders via Portal
 *
 * Usage:
 * ```tsx
 * <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
 *   <Modal.Header>
 *     <h2>Title</h2>
 *   </Modal.Header>
 *   <Modal.Body>
 *     Content here
 *   </Modal.Body>
 *   <Modal.Footer>
 *     <button onClick={handleClose}>Close</button>
 *   </Modal.Footer>
 * </Modal>
 * ```
 */
export default function Modal({
  isOpen,
  onClose,
  children,
  size = 'lg',
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = ''
}: ModalProps) {
  // Handle escape key
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && closeOnEscape) {
      onClose()
    }
  }, [onClose, closeOnEscape])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, handleEscape])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      onClose()
    }
  }

  return createPortal(
    <div
      className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 z-[9999] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`bg-white rounded-lg shadow-xl w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden flex flex-col ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

// Sub-components for consistent structure

interface ModalHeaderProps {
  children: ReactNode
  onClose?: () => void
  showCloseButton?: boolean
  className?: string
}

Modal.Header = function ModalHeader({
  children,
  onClose,
  showCloseButton = true,
  className = ''
}: ModalHeaderProps) {
  return (
    <div className={`flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10 ${className}`}>
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        {children}
      </div>
      {showCloseButton && onClose && (
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 ml-4"
          aria-label="Close modal"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
      )}
    </div>
  )
}

interface ModalBodyProps {
  children: ReactNode
  className?: string
}

Modal.Body = function ModalBody({ children, className = '' }: ModalBodyProps) {
  return (
    <div className={`p-6 overflow-y-auto flex-1 ${className}`}>
      {children}
    </div>
  )
}

interface ModalFooterProps {
  children: ReactNode
  className?: string
}

Modal.Footer = function ModalFooter({ children, className = '' }: ModalFooterProps) {
  return (
    <div className={`p-6 border-t border-gray-200 bg-gray-50 sticky bottom-0 ${className}`}>
      {children}
    </div>
  )
}
